import type { State } from '../configureStore'
import * as AutocompleteActionTypes from '../actionTypes/autocomplete.action.types'
import * as UserUsageActions from './userUsage.actions'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import {
  GraphQueryMgr,
  AutoCompleteQueryReq,
  AutoCompleteRespTypeEnum
} from '@src/endpoints/services/graphQueryMgr.service'
import type { AutoCompleteUsedInResp } from '@src/endpoints/services/commonHdr.service'
import { getDate } from '@src/utils/date.helpers'
import uuidv4 from 'uuid/v4'
import { toastInfo, toastError } from '@src/utils/toast'
import {
  formatAutocompleteTable,
  AutocompleteTable
} from '@src/utils/redux.tools'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { autocomplete_session_id } from '@src/constants/constants'
import {
  SearchResult,
  invalidInput
} from '../reducers/autocomplete.query/default.state'
import { isEmpty, orderBy } from 'lodash'

export type ActionTypes =
  | autocompleteSearchSuccessAction
  | searchResultsSuccessAction
  | searchQuerySaveAction
  | requestPendingAction
  | disposeDataAction
  | setSessionAction
  | disposeSessionAction
  | changeBatchSizeAction

export const autocompleteSearchSuccess = (
  searchData: string[],
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.AUTOCOMPLETE_SEARCH_SUCCESS,
    { searchData, isPending }
  )
type autocompleteSearchSuccessAction = ReturnType<
  typeof autocompleteSearchSuccess
>

export const searchResultsSuccess = (
  table?: SearchResult[] | AutocompleteTable[],
  searchData?: string[],
  hasNext?: boolean,
  hasPrev?: boolean,
  autoCompleteRespType?: AutoCompleteRespTypeEnum,
  sqlQueryUserUsage?: string,
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.SEARCH_RESULTS_SUCCESS,
    {
      table,
      searchData,
      hasNext,
      hasPrev,
      autoCompleteRespType,
      sqlQueryUserUsage,
      isPending
    }
  )
type searchResultsSuccessAction = ReturnType<typeof searchResultsSuccess>

export const searchQuerySave = (searchQuery: string) =>
  ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.SEARCH_QUERY_SAVE,
    {
      searchQuery
    }
  )
type searchQuerySaveAction = ReturnType<typeof searchQuerySave>

export const changeBatchSize = (batchSize: number) =>
  ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.CHANGE_BATCH_SIZE,
    {
      batchSize
    }
  )
type changeBatchSizeAction = ReturnType<typeof changeBatchSize>

export const disposeData = () =>
  ReduxHelpers.Actions.createPayloadAction(AutocompleteActionTypes.DISPOSE, {})
type disposeDataAction = ReturnType<typeof disposeData>

export const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.REQUEST_PENDING,
    {
      isPending
    }
  )
type requestPendingAction = ReturnType<typeof requestPending>

export const setSession = (sessionID: string, batchSize: number) => {
  sessionStorage.setItem(autocomplete_session_id, sessionID || '')
  return ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.SET_SESSION,
    {
      sessionID,
      batchSize
    }
  )
}

type setSessionAction = ReturnType<typeof setSession>

export const disposeSession = () => {
  sessionStorage.removeItem(autocomplete_session_id)
  return ReduxHelpers.Actions.createPayloadAction(
    AutocompleteActionTypes.DISPOSE_SESSION,
    {}
  )
}

type disposeSessionAction = ReturnType<typeof disposeSession>

interface AutocompleteSessionSearch {
  input?: string
  batchSize?: number
  showToastMessages?: boolean
}

export function autocompleteSessionSearch(options?: AutocompleteSessionSearch) {
  return (dispatch: any, getStore: () => State) => {
    const { autocomplete } = getStore()
    const { searchQuery, batchSize } = autocomplete
    if (!isEmpty(searchQuery)) {
      const pageSize = options?.batchSize || batchSize
      const sessionID = uuidv4()
      dispatch(closeCurrentSession())

      dispatch(
        autocompleteUserSearch({
          input: options?.input || searchQuery,
          isNext: true,
          sessionID,
          batchSize: pageSize,
          showToastMessages: options?.showToastMessages,
          requestPending
        })
      )
    }
  }
}

export function nextAutocompleteSessionSearch() {
  return (dispatch: any, getStore: () => State) => {
    const { autocomplete } = getStore()
    const sessionID = sessionStorage.getItem(autocomplete_session_id)
    const { searchQuery, batchSize } = autocomplete
    dispatch(
      autocompleteUserSearch({
        input: searchQuery,
        isNext: true,
        sessionID,
        batchSize,
        requestPending
      })
    )
  }
}

export function prevAutocompleteSessionSearch() {
  return (dispatch: any, getStore: () => State) => {
    const { autocomplete } = getStore()
    const sessionID = sessionStorage.getItem(autocomplete_session_id)
    const { searchQuery, batchSize } = autocomplete
    dispatch(
      autocompleteUserSearch({
        input: searchQuery,
        isNext: false,
        sessionID,
        batchSize,
        requestPending
      })
    )
  }
}

export function autocompleteGetOptions(input: string) {
  return (dispatch: any, getStore: () => State) =>
    dispatch(
      autocompleteUserSearch({
        input,
        showErrors: false,
        requestPending: null,
        showToastMessages: false
      })
    )
}

export function prepSession(query: string, callback?: () => void) {
  return (dispatch: any, getStore: () => State) => {
    const sessionID = sessionStorage.getItem(autocomplete_session_id)
    dispatch(saveSearchQuery(query))
    dispatch(releaseSession(sessionID, callback))
  }
}

export function closeCurrentSession(callback?: () => void) {
  return (dispatch: any, getStore: () => State) => {
    const sessionID = sessionStorage.getItem(autocomplete_session_id)
    if (!isEmpty(sessionID)) {
      dispatch(releaseSession(sessionID, callback))
    }
  }
}

export function releaseSession(sessionID: string, callback?: () => void) {
  return (dispatch: any, getStore: () => State) => {
    if (!isEmpty(sessionID)) {
      dispatch(disposeSession())
      GraphQueryMgr.CloseSearchSession({
        params: {
          req_hdr: {
            tid: uuidv4()
          },
          session_id: sessionID
        }
      }).then(res => {
        if (!isEmpty(res.error)) {
          toastError(res.error)
        }
        if (callback) {
          callback()
        }
      })
    } else if (callback) {
      callback()
    }
  }
}

interface AutocompleteUserSearch {
  input: string
  isNext?: boolean
  sessionID?: string
  batchSize?: number
  showToastMessages?: boolean
  showErrors?: boolean
  requestPending?: (isPending: boolean) => void
}

export function autocompleteUserSearch({
  input,
  isNext = false,
  sessionID = null,
  batchSize = undefined,
  showToastMessages = false,
  showErrors = true,
  requestPending
}: AutocompleteUserSearch) {
  return (dispatch: any, getStore: () => State) => {
    const { filter, auth, autocomplete } = getStore()
    const { batchSize: defaultBatchSize } = autocomplete
    const { tenant } = auth
    const { account_info = [] } = tenant || {}
    const params: AutoCompleteQueryReq = {
      req_hdr: {
        tid: uuidv4()
      },
      input,
      session_props: {
        session_id: sessionID || undefined,
        batch_size: batchSize || defaultBatchSize,
        is_next: isNext
      }
    }
    dispatch(searchQuerySave(input))
    if (typeof requestPending === 'function') {
      dispatch(requestPending(true))
    }
    GraphQueryMgr.AutoCompleteQueryUser({
      makeRequestOptions: {
        handleResponseErrors: res => {}
      },
      params: paramsWithFilter(params, filter)
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            if (typeof requestPending === 'function') {
              dispatch(requestPending(false))
            }
            dispatch(autocompleteSearchSuccess([invalidInput]))
          },
          showErrors
        )
      )
      .then(res => {
        dispatch(disposeData())
        dispatch(autocompleteSearchSuccess(orderBy(res.options) || []))
        const { usage_resp, resp_type } = res
        const { start_timestamp, end_timestamp, query } = (usage_resp ||
          {}) as AutoCompleteUsedInResp
        if (!isEmpty(start_timestamp) && !isEmpty(end_timestamp)) {
          dispatch(
            UserUsageActions.setTimestamp({
              startTime: getDate(start_timestamp),
              endTime: getDate(end_timestamp)
            })
          )
        }
        if (!isEmpty(query)) {
          dispatch(searchResultsSuccess([], [], false, false, resp_type, query))
        }
        if (!isEmpty(res.result_table)) {
          dispatch(setSession(sessionID, batchSize))
          const table = res.result_table
            ? formatAutocompleteTable(res.result_table, account_info)
            : []
          dispatch(
            searchResultsSuccess(
              table,
              res.options,
              !!res.has_next,
              !!res.has_prev,
              resp_type,
              query
            )
          )
          if (showToastMessages) {
            if (!isEmpty(res.result_table) && table.length === 0) {
              toastInfo('No matching records')
            }
          }
        }
        if (typeof requestPending === 'function') {
          dispatch(requestPending(false))
        }
      })
      .catch((res: Response) => {
        if (typeof requestPending === 'function') {
          dispatch(requestPending(false))
        }
      })
  }
}

export function saveSearchQuery(query: string) {
  return (dispatch: any, getStore: () => State) => {
    dispatch(searchQuerySave(query))
  }
}
